import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { 
  Settings, ChevronLeft, Info, User, Palette, Globe, Shield, 
  Phone, FileText, Play, Key, Check, Sun, Moon, Upload, X, ListVideo
} from "lucide-react";
import { useT, SUPPORTED_LANGS } from "../i18n";
import { useTheme } from "../contexts/ThemeContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const navigate = useNavigate();
  const [deviceKey, setDeviceKey] = useState("");
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [activeSection, setActiveSection] = useState("device");
  const [currentTime, setCurrentTime] = useState(new Date());

  // i18n + theme
  const { lang, setLang, t } = useT();
  const {
    mode, toggleMode,
    wallpaper, setWallpaper,
    wallpapers, addCustomWallpaper, removeCustomWallpaper,
  } = useTheme();

  const onWallpaperUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const item = addCustomWallpaper(reader.result, file.name);
      setWallpaper(item.url);
      toast.success(t("Wallpaper added") !== "Wallpaper added"
        ? t("Wallpaper added")
        : "Wallpaper added");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // languages come from SUPPORTED_LANGS now

  const menuItems = [
    { id: 'device', icon: Info, label: t('Device Info') },
    { id: 'account', icon: User, label: t('User Account') },
    { id: 'theme', icon: Palette, label: t('Change Theme') },
    { id: 'language', icon: Globe, label: t('Change Language') },
    { id: 'parental', icon: Shield, label: t('Parental Control') },
    { id: 'contact', icon: Phone, label: t('Contact Us') },
    { id: 'legal', icon: FileText, label: t('Legal Notice') },
    { id: 'player', icon: Play, label: t('Video Player Setting') },
    { id: 'playlist', icon: Key, label: t('Playlist Code') },
  ];

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const storedKey = localStorage.getItem("deviceKey");
    if (storedKey) {
      setDeviceKey(storedKey);
      axios.get(`${API}/device/status/${storedKey}`)
        .then(res => setDeviceStatus(res.data))
        .catch(() => {});
    }
  }, []);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' 
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'device':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-cyan-400 border-b border-white/10 pb-2">Device Info</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-white/60">Device ID</div>
              <div className="text-white font-mono">{deviceKey}</div>
              <div className="text-white/60">Activation Code</div>
              <div className="text-white font-mono">{deviceStatus?.activation_code || 'N/A'}</div>
              <div className="text-white/60">Package Name</div>
              <div className="text-white">{deviceStatus?.is_paid ? 'LIFETIME' : 'TRIAL'}</div>
              <div className="text-white/60">Status</div>
              <div className={`font-medium ${deviceStatus?.status === 'active' ? 'text-green-400' : 'text-yellow-400'}`}>
                {deviceStatus?.status?.toUpperCase()}
              </div>
            </div>
          </div>
        );
      
      case 'account':
        return (
          <div className="space-y-4" data-testid="account-panel">
            <h3 className="text-lg font-medium text-cyan-400 border-b border-white/10 pb-2">Account</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-white/60">Device ID</div>
              <div className="text-white font-mono">{deviceKey}</div>
              <div className="text-white/60">Expire Date</div>
              <div className="text-white">
                {deviceStatus?.is_paid ? 'LIFETIME' : deviceStatus?.trial_end ? new Date(deviceStatus.trial_end).toLocaleDateString() : 'N/A'}
              </div>
              <div className="text-white/60">App Status</div>
              <div className="text-green-400 font-medium">
                {deviceStatus?.is_paid ? 'LIFE TIME' : `TRIAL (${deviceStatus?.days_remaining || 0} days)`}
              </div>
            </div>

            <div className="pt-4 mt-4 border-t border-white/10">
              <button
                data-testid="manage-playlists-btn"
                onClick={() => navigate('/welcome')}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-cyan-500 text-black font-semibold hover:bg-cyan-400 transition-colors"
              >
                <ListVideo className="w-4 h-4" />
                Manage playlists
              </button>
              <p className="text-xs text-white/50 mt-2 text-center">
                Add a new playlist or remove an existing one.
              </p>
            </div>
          </div>
        );
      
      case 'theme':
        return (
          <div className="space-y-6" data-testid="theme-panel">
            {/* Dark / Light color mode toggle */}
            <div className="flex items-center justify-between border border-white/10 rounded-xl p-4 bg-black/30">
              <div>
                <div className="text-cyan-400 font-medium">{t('Change Theme')}</div>
                <div className="text-sm text-white/60 mt-1">
                  {mode === 'dark' ? 'Dark mode' : 'Light mode'}
                </div>
              </div>
              <button
                data-testid="toggle-color-mode-btn"
                onClick={toggleMode}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-black font-medium rounded-lg hover:bg-cyan-400 transition-colors"
              >
                {mode === 'dark'
                  ? (<><Sun className="w-4 h-4" /> Light</>)
                  : (<><Moon className="w-4 h-4" /> Dark</>)}
              </button>
            </div>

            {/* Wallpaper grid */}
            <div>
              <h3 className="text-lg font-medium text-cyan-400 border-b border-white/10 pb-2 mb-4">
                {t('Select Theme') !== 'Select Theme' ? t('Select Theme') : 'Wallpaper'}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {wallpapers.map((w) => {
                  const active = (wallpaper || null) === (w.url || null);
                  const bg = w.url
                    ? `url(${w.url}) center/cover no-repeat`
                    : 'linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%)';
                  return (
                    <button
                      key={w.id}
                      data-testid={`wallpaper-${w.id}`}
                      onClick={() => setWallpaper(w.url)}
                      className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                        active ? 'border-cyan-400 scale-105' : 'border-transparent hover:border-white/30'
                      }`}
                      style={{ background: bg }}
                    >
                      {active && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Check className="w-6 h-6 text-cyan-400" />
                        </div>
                      )}
                      <span className="absolute bottom-1 left-1 right-1 text-[10px] bg-black/60 text-white rounded px-1 py-0.5 truncate text-left">
                        {w.name}
                      </span>
                      {w.custom && (
                        <span
                          role="button"
                          data-testid={`wallpaper-remove-${w.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (wallpaper === w.url) setWallpaper(null);
                            removeCustomWallpaper(w.id);
                          }}
                          className="absolute top-1 right-1 bg-red-600/80 text-white rounded w-5 h-5 grid place-items-center"
                        >
                          <X className="w-3 h-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <label
                data-testid="wallpaper-upload-btn"
                className="mt-4 inline-flex items-center gap-2 px-3 py-2 border border-cyan-400/40 text-cyan-300 rounded-lg cursor-pointer hover:bg-cyan-400/10 transition-colors text-sm"
              >
                <Upload className="w-4 h-4" />
                Upload your own
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onWallpaperUpload}
                  data-testid="wallpaper-upload-input"
                />
              </label>
            </div>
          </div>
        );
      
      case 'language':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-cyan-400 border-b border-white/10 pb-2">{t('Select Language')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-1">
              {SUPPORTED_LANGS.map((L) => (
                <button
                  key={L.code}
                  onClick={() => setLang(L.code)}
                  className={`px-4 py-2 text-left rounded transition-all flex items-center gap-3 ${
                    lang === L.code
                      ? 'bg-cyan-500 text-black font-medium'
                      : 'hover:bg-white/10 text-white/80'
                  }`}
                  data-testid={`lang-${L.code}`}
                >
                  <span className="text-xl">{L.flag}</span>
                  <span>{L.name}</span>
                </button>
              ))}
            </div>
          </div>
        );
      
      case 'parental':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-cyan-400 border-b border-white/10 pb-2">Parent Control</h3>
            <ParentalControlPanel deviceKey={deviceKey} />
          </div>
        );
      
      case 'contact':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-cyan-400 border-b border-white/10 pb-2">Contact Us</h3>
            <p className="text-white/80">
              If you're having a problem with our service and need help, go to: <span className="text-cyan-400">https://nettv.app</span>
            </p>
            <p className="text-white/80">
              For suggestions on improving our service, send email to <span className="text-cyan-400">info@nettv.app</span> and we will review it.
            </p>
          </div>
        );
      
      case 'legal':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-cyan-400 border-b border-white/10 pb-2">Legal Notice</h3>
            <div className="text-white/70 text-sm space-y-4 max-h-[400px] overflow-y-auto">
              <p>NetTV owns the legal rights to the application and has built the NetTV app as a premium app.</p>
              <p>This page is used to inform visitors regarding our policies with the collection, use, and disclosure of Personal Information if anyone decided to use our Services.</p>
              <p><strong>Information Collection and Use</strong></p>
              <p>For a better experience, while using our Service, we may require you to provide us with certain personally identifiable information. The information that we request will be retained by us and used as described in this privacy policy.</p>
              <p><strong>Log Data</strong></p>
              <p>We want to inform you that whenever you use our Service, in a case of an error in the app we collect data and information through third party products on your device called Log Data.</p>
            </div>
          </div>
        );
      
      case 'player':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-cyan-400 border-b border-white/10 pb-2">Video Player Setting</h3>
            <VideoPlayerSettingsPanel deviceKey={deviceKey} />
          </div>
        );
      
      case 'playlist':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-cyan-400 border-b border-white/10 pb-2">Playlist Code</h3>
            <PlaylistCodePanel deviceKey={deviceKey} navigate={navigate} />
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen nettv-bg text-white">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 bg-black/30">
        <div className="nettv-logo">
          <img src="/nettv-logo.png" alt="NetTV" className="h-8" />
        </div>
        
        <div className="flex items-center gap-6 text-sm">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-white/60 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-white/80">
            {formatTime(currentTime)} {formatDate(currentTime)}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex gap-0 p-6 h-[calc(100vh-80px)]">
        {/* Sidebar Menu */}
        <aside className="w-64 bg-black/20 rounded-l-xl">
          <nav className="py-4">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full px-6 py-3 text-left flex items-center gap-3 transition-all ${
                  activeSection === item.id 
                    ? 'bg-yellow-400 text-black font-medium' 
                    : 'text-white/80 hover:bg-white/5'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content Panel */}
        <section className="flex-1 bg-black/30 rounded-r-xl p-6">
          {renderContent()}
        </section>
      </main>
    </div>
  );
};

// =============================================================
// Sub-panels: Parental, Video Player Settings, Playlist Code
// =============================================================

const ParentalControlPanel = ({ deviceKey }) => {
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [hasPin, setHasPin] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!deviceKey) return;
    axios.get(`${API}/settings/${deviceKey}`).then((res) => {
      setHasPin(res.data?.parental_pin === "set");
    }).catch(() => {});
  }, [deviceKey]);

  const submit = async () => {
    if (!/^\d{4}$/.test(newPin)) {
      toast.error("New PIN must be exactly 4 digits"); return;
    }
    if (newPin !== confirmPin) {
      toast.error("PIN confirmation does not match"); return;
    }
    if (hasPin && currentPin.length === 0) {
      toast.error("Enter your current PIN"); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/settings/${deviceKey}/parental-pin`, {
        current_pin: hasPin ? currentPin : null,
        new_pin: newPin,
      });
      toast.success("Parental PIN updated");
      setCurrentPin(""); setNewPin(""); setConfirmPin("");
      setHasPin(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update PIN");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="parental-panel">
      {hasPin && (
        <div>
          <label className="text-white/60 text-sm">Current PIN:</label>
          <input
            type="password" inputMode="numeric" maxLength={4}
            value={currentPin}
            onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
            className="w-full mt-1 px-3 py-2 bg-black/50 border border-white/20 rounded text-white"
            data-testid="parental-current-pin"
          />
        </div>
      )}
      <div>
        <label className="text-white/60 text-sm">New PIN (4 digits):</label>
        <input
          type="password" inputMode="numeric" maxLength={4}
          value={newPin}
          onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
          className="w-full mt-1 px-3 py-2 bg-black/50 border border-white/20 rounded text-white"
          data-testid="parental-new-pin"
        />
      </div>
      <div>
        <label className="text-white/60 text-sm">Confirm PIN:</label>
        <input
          type="password" inputMode="numeric" maxLength={4}
          value={confirmPin}
          onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
          className="w-full mt-1 px-3 py-2 bg-black/50 border border-white/20 rounded text-white"
          data-testid="parental-confirm-pin"
        />
      </div>
      <button
        onClick={submit}
        disabled={saving}
        data-testid="parental-ok-btn"
        className="px-6 py-2 bg-cyan-500 text-black font-medium rounded hover:bg-cyan-400 transition-colors disabled:opacity-50"
      >
        {saving ? "Saving…" : "OK"}
      </button>
    </div>
  );
};

const PLAYER_PREF_KEY = "nettv.player_prefs";

const SegBtn = ({ label, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1 text-sm rounded transition-colors ${
      active ? "bg-cyan-500 text-black" : "bg-white/10 text-white hover:bg-white/20"
    }`}
  >
    {label}
  </button>
);

const VideoPlayerSettingsPanel = ({ deviceKey }) => {
  const [prefs, setPrefs] = useState(() => {
    try {
      const cached = localStorage.getItem(PLAYER_PREF_KEY);
      if (cached) return JSON.parse(cached);
    } catch (e) { /* ignore */ }
    return { brightness: 100, end_of_video: "Back To", subtitle_size: "Default", time_format: "12" };
  });

  useEffect(() => {
    if (!deviceKey) return;
    axios.get(`${API}/settings/${deviceKey}`).then((res) => {
      const d = res.data || {};
      setPrefs((p) => ({
        ...p,
        brightness: typeof d.video_brightness === "number" ? d.video_brightness : p.brightness,
        end_of_video: d.end_of_video || p.end_of_video,
        subtitle_size: d.subtitle_size || p.subtitle_size,
        time_format: d.time_format || p.time_format,
      }));
    }).catch(() => {});
  }, [deviceKey]);

  const persist = (next) => {
    setPrefs(next);
    try { localStorage.setItem(PLAYER_PREF_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
    if (!deviceKey) return;
    axios.put(`${API}/settings/${deviceKey}`, {
      video_brightness: next.brightness,
      end_of_video: next.end_of_video,
      subtitle_size: next.subtitle_size,
      time_format: next.time_format,
    }).catch(() => {});
  };

  return (
    <div className="space-y-4" data-testid="player-settings-panel">
      <div className="flex items-center justify-between">
        <span className="text-white/80">Video Brightness</span>
        <div className="flex items-center gap-3">
          <input
            type="range" min="20" max="100" value={prefs.brightness}
            data-testid="player-brightness"
            onChange={(e) => persist({ ...prefs, brightness: Number(e.target.value) })}
            className="w-32"
          />
          <span className="text-xs text-white/60 w-10 text-right">{prefs.brightness}%</span>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-white/80">End Of Video</span>
        <div className="flex gap-2">
          {["Back To", "Replay"].map((opt) => (
            <SegBtn
              key={opt} label={opt}
              active={prefs.end_of_video === opt}
              onClick={() => persist({ ...prefs, end_of_video: opt })}
              testId={`player-end-${opt.toLowerCase().replace(/\s+/g, "-")}`}
            />
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-white/80">Subtitle Text Size</span>
        <div className="flex gap-2">
          {["Small", "Medium", "Default", "Large"].map((opt) => (
            <SegBtn
              key={opt} label={opt}
              active={prefs.subtitle_size === opt}
              onClick={() => persist({ ...prefs, subtitle_size: opt })}
              testId={`player-subtitle-${opt.toLowerCase()}`}
            />
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-white/80">Time Format</span>
        <div className="flex gap-2">
          {["12", "24"].map((opt) => (
            <SegBtn
              key={opt} label={opt}
              active={prefs.time_format === opt}
              onClick={() => persist({ ...prefs, time_format: opt })}
              testId={`player-time-${opt}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const PlaylistCodePanel = ({ deviceKey, navigate }) => {
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!/^[A-Z0-9]{4,12}$/.test(trimmed)) {
      toast.error("Playlist code must be 4–12 letters/digits"); return;
    }
    setBusy(true);
    try {
      const res = await axios.get(`${API}/device/status/${trimmed}`);
      if (!res.data || !["active", "trial"].includes(res.data.status)) {
        toast.error("Code not valid or no active trial");
        return;
      }
      // Optional: verify parental PIN on this device before switching
      if (pin) {
        const v = await axios.post(`${API}/settings/${deviceKey}/verify-pin`, { pin });
        if (!v.data?.valid) { toast.error("Invalid PIN"); return; }
      }
      localStorage.setItem("deviceKey", trimmed);
      toast.success("Playlist code applied");
      navigate("/menu");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply playlist code");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="playlist-code-panel">
      <div>
        <label className="text-white/60 text-sm">Playlist Code (device key):</label>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="w-full mt-1 px-3 py-2 bg-black/50 border border-white/20 rounded text-white font-mono"
          data-testid="playlist-code-input"
          placeholder="e.g. ABC123"
        />
      </div>
      <div>
        <label className="text-white/60 text-sm">PIN (optional, parental):</label>
        <input
          type="password" inputMode="numeric" maxLength={4}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          className="w-full mt-1 px-3 py-2 bg-black/50 border border-white/20 rounded text-white"
          data-testid="playlist-pin-input"
        />
      </div>
      <button
        onClick={submit} disabled={busy}
        data-testid="playlist-code-ok-btn"
        className="px-6 py-2 bg-cyan-500 text-black font-medium rounded hover:bg-cyan-400 transition-colors disabled:opacity-50"
      >
        {busy ? "Applying…" : "OK"}
      </button>
    </div>
  );
};

export default SettingsPage;
